/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Divider, useMediaQuery, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight, FilterList } from '@mui/icons-material';
import { IndianZones } from '../../../helper/global';
import CheckBoxDropdown from './CheckBoxDropdown';
import Pill from '../../Common/Pill';
import CustomModal from '../../Common/CustomModal';

const SideFilter = ({
  tourDuration,
  indianData,
  themeFilter,
  adventureTag,
  adventuresData,
  selectedState,
  sideFilterInitialItems,
  onCheckedItemsChange,
  selectedZone,
  tourCategory,
  isBreadcrumbClicked,
  setIsBreadcrumbClicked,
  pathSegments
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const previousPathnameRef = useRef(location.pathname);
  const [isOpenDuration, setIsOpenDuration] = useState(true);
  const [isOpenTheme, setIsOpenTheme] = useState(true);
  const [isOpenAdventure, setIsOpenAdventure] = useState(true);
  const [isOpenIndia, setIsOpenIndia] = useState(true);
  const selectedTheme = themeFilter?.find((item) => item.isSelected);
  const [open, setOpen] = useState(false);

  const [checkedItems, setCheckedItems] = useState(() => {
    const initialItems = {
      'Tour Duration': { ...(sideFilterInitialItems?.['Tour Duration'] || {}) },
      'Indian Tours': { ...(sideFilterInitialItems?.['Indian Tours'] || {}) },
      'Tour Theme': { ...(sideFilterInitialItems?.['Tour Theme'] || {}) },
      Adventures: { ...(sideFilterInitialItems?.['Adventures'] || {}) }
    };

    if (Object.keys(initialItems['Indian Tours']).length === 0) {
      initialItems['Indian Tours'] = (indianData || []).reduce((acc, { value }) => {
        acc[value] = false;
        return acc;
      }, {});
    }

    if (Object.keys(initialItems['Tour Theme']).length === 0) {
      initialItems['Tour Theme'] = (themeFilter || []).reduce((acc, { value }) => {
        acc[value] = false;
        return acc;
      }, {});
    }
    return initialItems;
  });

  const anyChecked = Object.values(checkedItems).some((category) =>
    Object.values(category).some((value) => value === true)
  );

  useEffect(() => {
    const hasThemeInPath = location.pathname.includes('themed');
    const previousPathname = previousPathnameRef.current;

    if (previousPathname !== location.pathname) {
      if (!hasThemeInPath) {
        setCheckedItems((prevCheckedItems) => ({
          ...prevCheckedItems,
          'Tour Theme': {}
        }));
      }
    }
    previousPathnameRef.current = location.pathname;
  }, [location.pathname, sideFilterInitialItems]);

  useEffect(() => {
    if (isBreadcrumbClicked) {
      handleClearAll();
      setIsBreadcrumbClicked(false);
    }
  }, [isBreadcrumbClicked]);

  useEffect(() => {
    const updateCheckedItems = () => {
      const newCheckedItems = { ...checkedItems };
      if (adventureTag) {
        newCheckedItems['Adventures'][adventureTag] = true;
      }

      if (selectedTheme) {
        newCheckedItems['Tour Theme'][selectedTheme.value] = selectedTheme.isSelected;
      }

      if (selectedState) {
        if (Object.keys(newCheckedItems['Indian Tours']).length === 0) {
          newCheckedItems['Indian Tours'][selectedState] = true;
        } else {
          Object.keys(newCheckedItems['Indian Tours']).forEach((key) => {
            newCheckedItems['Indian Tours'][key] = key === selectedState ? true : false;
          });
          newCheckedItems['Indian Tours'][selectedState] = true;
        }
      }

      if (selectedZone && tourCategory === 'Domestic') {
        const statesInZone = IndianZones[selectedZone] || [];
        const updatedIndianTours = Object.keys(newCheckedItems['Indian Tours']).reduce(
          (acc, state) => {
            acc[state] = statesInZone.includes(state);
            return acc;
          },
          {}
        );
        newCheckedItems['Indian Tours'] = updatedIndianTours;
      }

      // Checked indian state based on selectedZone
      if (
        selectedZone &&
        (tourCategory === '' || tourCategory === 'Domestic') &&
        selectedState === '' &&
        indianData?.length > 0
      ) {
        const statesInZone = IndianZones[selectedZone] || [];
        const updatedIndianTours = {};
        if (statesInZone.length > 0) {
          statesInZone.forEach((state) => {
            updatedIndianTours[state] = true;
          });
        }
        newCheckedItems['Indian Tours'] = updatedIndianTours;
      }

      // Checked all indian state if pathSegments[0] = india
      if (
        pathSegments[0] === 'india' &&
        selectedZone === '' &&
        tourCategory === '' &&
        selectedState === '' &&
        indianData?.length > 0
      ) {
        const updatedIndianTours = {};
        if (indianData?.length > 0) {
          indianData.forEach((state) => {
            updatedIndianTours[state.value] = true;
          });
        }
        newCheckedItems['Indian Tours'] = updatedIndianTours;
      }
      setCheckedItems(newCheckedItems);
    };
    updateCheckedItems();
  }, [adventureTag, selectedTheme, selectedState, selectedZone, tourCategory, anyChecked]);

  useEffect(() => {
    if (tourCategory === 'Domestic' && selectedZone) {
      const statesInZone = IndianZones[selectedZone] || [];
      setCheckedItems((prevState) => {
        if (!prevState['Indian Tours']) {
          return prevState;
        }
        const updatedIndianTours = Object.keys(prevState['Indian Tours']).reduce((acc, state) => {
          acc[state] = statesInZone.includes(state);
          return acc;
        }, {});

        return {
          ...prevState,
          'Indian Tours': updatedIndianTours
        };
      });
    }
  }, [selectedZone, tourCategory]);

  useEffect(() => {
    onCheckedItemsChange(checkedItems);
  }, [checkedItems, onCheckedItemsChange, selectedState]);

  const handleCloseModal = () => setOpen(false);
  const handleOpenModal = () => setOpen(true);
  const handleToggleDuration = () => setIsOpenDuration(!isOpenDuration);
  const handleToggleIndia = () => setIsOpenIndia(!isOpenIndia);
  const handleToggleTheme = () => setIsOpenTheme(!isOpenTheme);
  const handleToggleAdventure = () => setIsOpenAdventure(!isOpenAdventure);

  const handleRadioChange = (event, groupTitle) => {
    const { value } = event.target;
    setCheckedItems((prevState) => {
      const updatedCheckedItems = { ...prevState };
      const isCurrentlySelected = updatedCheckedItems[groupTitle][value];
      Object.keys(updatedCheckedItems[groupTitle]).forEach((key) => {
        updatedCheckedItems[groupTitle][key] = false;
      });

      if (!isCurrentlySelected) {
        updatedCheckedItems[groupTitle][value] = true;
      }
      return updatedCheckedItems;
    });
  };

  const handleCheckboxChange = (event, groupTitle) => {
    const { name, checked } = event.target;
    setCheckedItems((prevState) => {
      const updatedCheckedItems = { ...prevState };
      return {
        ...updatedCheckedItems,
        [groupTitle]: {
          ...updatedCheckedItems[groupTitle],
          [name]: checked
        }
      };
    });
  };

  //TODO: remove commented code for handleClearAll() 1st if user want to render only those packages based on url and remove 2nd handleClearAll()
  // const handleClearAll = () => {
  //   setCheckedItems(sideFilterInitialItems);
  // };

  const handleClearAll = () => {
    setCheckedItems({
      'Tour Budget': {},
      'Tour Duration': {},
      'Indian Tours': {},
      'World Tours': {},
      'Tour Theme': {},
      Adventures: {}
    });
    // setCheckedItems(sideFilterInitialItems);
    setIsBreadcrumbClicked(false);
    navigate('/explore');
  };

  useEffect(() => {
    if (location.pathname === '/explore') {
      setCheckedItems({
        'Tour Budget': {},
        'Tour Duration': {},
        'Indian Tours': {},
        'World Tours': {},
        'Tour Theme': {},
        Adventures: {}
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    onCheckedItemsChange(checkedItems);
  }, [checkedItems, onCheckedItemsChange, selectedState]);

  const handleRemovePill = (optionValue) => {
    const updatedCheckedItems = { ...checkedItems };
    for (const [groupTitle, groupItems] of Object.entries(updatedCheckedItems)) {
      if (groupItems[optionValue] !== undefined) {
        updatedCheckedItems[groupTitle][optionValue] = false;
        break;
      }
    }
    setCheckedItems(updatedCheckedItems);
  };

  const generatePills = () => {
    // eslint-disable-next-line no-unused-vars
    const selectedValues = Object.entries(checkedItems).reduce((acc, [groupTitle, groupItems]) => {
      Object.keys(groupItems).forEach((key) => {
        if (groupItems[key]) acc.push(key);
      });
      return acc;
    }, []);
    return selectedValues.map((value) => (
      <Pill key={value} title={value} onClick={() => handleRemovePill(value)} />
    ));
  };

  const hasSelectedItems = () =>
    Object.values(checkedItems).some((groupItems) =>
      Object.values(groupItems).some((isChecked) => isChecked)
    );

  const dropdownsData = [
    {
      title: 'Tour Duration',
      options: tourDuration,
      isOpen: isOpenDuration,
      isCheckbox: true,
      handleToggle: handleToggleDuration
    },
    {
      title: 'Indian Tours',
      options: indianData,
      isOpen: isOpenIndia,
      isCheckbox: true,
      handleToggle: handleToggleIndia
    },
    {
      title: 'Tour Theme',
      options: themeFilter,
      isOpen: isOpenTheme,
      isCheckbox: false,
      handleToggle: handleToggleTheme
    }
    // {
    //   title: 'Adventures',
    //   options: adventuresData,
    //   isOpen: isOpenAdventure,
    //   isCheckbox: false,
    //   handleToggle: handleToggleAdventure
    // }
  ];

  const filterContent = (
    <Box
      className="filter-your-tour-left"
      bgcolor="white"
      sx={{
        borderTopLeftRadius: { xs: 12, md: 12 },
        borderTopRightRadius: { xs: 12, md: 12 },
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        overflow: 'hidden',
        marginBottom: '60px'
      }}>
      <Box
        sx={{
          width: { xs: '100%', md: '220px', lg: '300px' },
          position: 'relative',
          backgroundColor: '#FFF'
        }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          pl={isMobile ? '0px' : '25px'}
          pt={isMobile ? '50px' : '15px'}>
          <ChevronLeft
            sx={{
              display: isMobile ? 'block' : 'none',
              cursor: 'pointer',
              width: 20,
              height: 20
            }}
            onClick={handleCloseModal}
          />
          <Typography
            fontSize={16}
            fontFamily="gothamBold"
            lineHeight="26px"
            color="#000000D9"
            flex={1}
            ml={isMobile && 2}>
            {isMobile ? 'Filter Your Trip!' : 'Filter your Tour'}
          </Typography>
          {hasSelectedItems() ? (
            <Button
              onClick={handleClearAll}
              sx={{
                fontSize: '12px',
                lineHeight: '16px',
                fontFamily: 'poppinsRegular',
                color: '#E41E25CC',
                textTransform: 'capitalize'
              }}>
              Clear All
            </Button>
          ) : null}
        </Box>
        {hasSelectedItems() ? (
          <Box
            mt="22px"
            display="flex"
            flexWrap="wrap"
            gap={1}
            height={80}
            pb={2.75}
            px="22px"
            sx={{ overflowY: 'scroll' }}>
            {generatePills()}
          </Box>
        ) : null}
        <Box sx={{ borderTop: '1px solid #0000001A !important' }}>
          {dropdownsData.map((dropdown, index) => (
            <Box key={index}>
              <CheckBoxDropdown
                title={dropdown.title}
                options={dropdown.options}
                selectedValues={checkedItems}
                handleRadioChange={(event) => handleRadioChange(event, dropdown.title)}
                handleCheckboxChange={(event) => handleCheckboxChange(event, dropdown.title)}
                isOpen={dropdown.isOpen}
                handleToggle={dropdown.handleToggle}
                isCheckbox={dropdown.isCheckbox}
              />
              {index !== dropdownsData.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>
        <Button
          sx={{
            width: '100%',
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#E41E25',
            py: '13px',
            justifyContent: 'center',
            alignItems: 'center',
            variant: 'contained',
            display: isMobile ? 'flex' : 'none',
            ':hover': {
              backgroundColor: '#E41E25'
            }
          }}
          onClick={handleCloseModal}>
          <Typography
            fontFamily="gothamBold"
            fontSize={18}
            lineHeight="28px"
            color="#fff"
            mr="12px">
            Apply Filter
          </Typography>
          <ChevronRight sx={{ color: '#fff', width: 15, height: 15 }} />
          <ChevronRight sx={{ color: '#fff', width: 15, height: 15 }} />
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <>
          <Button
            onClick={handleOpenModal}
            startIcon={<FilterList />}
            variant="outlined"
            sx={{
              border: '1px solid #0000004D',
              color: '#000000CC',
              mb: 2,
              textTransform: 'capitalize'
            }}>
            Filter
          </Button>
          <CustomModal
            title={'Custom Modal'}
            open={open}
            onClose={handleCloseModal}
            sx={{
              height: '100vh',
              backgroundColor: '#fff',
              p: '21px',
              overflow: 'scroll',
              pb: { xs: '75px', md: 0 },
              width: '100%',
              marginTop: 'auto'
            }}>
            <>{filterContent}</>
          </CustomModal>
        </>
      ) : (
        <>{filterContent}</>
      )}
    </>
  );
};

export default SideFilter;
